import { clearAnalysisFieldOverride, getAiFieldText, markAnalysisFieldOverrides, SERVITUT_ANALYSIS_FIELDS } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { statusFromConfidence } from "src/modules/ANNModule/ANNServitutProvenance";
import type { LLMAnalysisResultItem } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { resolveAnalysisRelevantPages } from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import {
   fieldsFromAnalysis,
   projectAnnOntoRow,
   rebuildFieldSlot,
} from "../model/projectAnn";
import { emptyFields, emptyFieldSlot, isDigitalRow, isFetched, needsLandRegistryRebind, registryBindingFor, withDeferredAktFetchJob, type ServitutRow } from "../model/row";
import { aktPagesEqual, emptyPageModel, normalizeAktPages, pageModelFromLists } from "../model/pages";
import {
   initialServitutReportState,
   mapRow,
   type ServitutReportState,
} from "../model/report";
import { ServitutReportActionType, type ContentRowResult, type LocateRowResult, type ServitutReportAction } from "./actions";

function setJobs(state: ServitutReportState, rowIds: string[], job: ServitutRow["job"]): ServitutReportState {
   let next = state;
   rowIds.forEach((id) => {
      next = mapRow(next, id, (row) => ({ ...row, job }));
   });
   return next;
}

function fetchingRowIds(rows: Record<string, ServitutRow>): string[] {
   return Object.values(rows).filter((row) => row.job === "fetching").map((row) => row.id);
}

function mergeAktFetchIds(state: ServitutReportState, rowIds: string[]): ServitutReportState {
   if (!rowIds.length) {
      return state;
   }
   const next = new Set(state.aktFetchIds);
   rowIds.forEach((id) => next.add(id));
   return { ...state, aktFetchIds: Array.from(next) };
}

function settleAktFetchBatch(state: ServitutReportState): ServitutReportState {
   if (!state.aktFetchIds.length) {
      return state;
   }
   if (state.aktFetchIds.some((id) => state.rows[id]?.job === "fetching")) {
      return state;
   }
   return { ...state, aktFetchIds: [] };
}

function addIds(list: string[], ids: string[]): string[] {
   const next = new Set(list);
   ids.forEach((id) => next.add(id));
   return Array.from(next);
}

function removeIds(list: string[], ids: string[]): string[] {
   const drop = new Set(ids);
   return list.filter((id) => !drop.has(id));
}

function applyLocate(row: ServitutRow, item: LocateRowResult["item"]): ServitutRow {
   const digital = isDigitalRow(row);
   const implied = resolveAnalysisRelevantPages(
      item.pages_kept,
      item.original_pages || item.extracted_pages,
      digital,
   );
   const pagesChanged = !aktPagesEqual(row.analysis?.item.pages_kept, item.pages_kept);
   const hadContent = Boolean(row.analysis && !row.analysis.stale && (
      row.analysis.item.summary || row.analysis.item.servitut_type || row.analysis.item.paataleberettiget
   ));
   const selected = implied.length || !digital ? implied : row.pages.selected;
   const next: ServitutRow = {
      ...row,
      job: "idle",
      analysis: {
         item: {
            ...row.analysis?.item,
            ...item,
            id: item.id || row.analysis?.item.id || row.id,
            summary: row.analysis?.item.summary,
            servitut_type: row.analysis?.item.servitut_type,
            servitut_type_confidence: row.analysis?.item.servitut_type_confidence,
            paataleberettiget: row.analysis?.item.paataleberettiget,
            paataleberettiget_confidence: row.analysis?.item.paataleberettiget_confidence,
            paataleberettiget_resolution: row.analysis?.item.paataleberettiget_resolution,
            case_relevant: row.analysis?.item.case_relevant,
            case_relevance_reasoning: row.analysis?.item.case_relevance_reasoning,
            case_relevance_confidence: row.analysis?.item.case_relevance_confidence,
            case_description: row.analysis?.item.case_description,
         },
         stale: hadContent && pagesChanged,
      },
      pages: pageModelFromLists(
         selected,
         normalizeAktPages(item.pages_kept),
         digital && !normalizeAktPages(item.pages_kept).length ? "entire_akt" : (selected.length ? "selection" : "missing"),
      ),
      review: row.review.state === "rejected" ? row.review : { state: "unchecked", origin: row.review.origin },
   };
   return {
      ...next,
      fields: fieldsFromAnalysis(next, next.analysis?.item, { hasCaseDescription: Boolean(next.analysis?.item.case_description) }),
   };
}

function applyContent(row: ServitutRow, result: ContentRowResult): ServitutRow {
   const item = {
      ...row.analysis?.item,
      ...result.item,
      id: result.item.id || row.id,
      pages_kept: row.pages.selected.length ? row.pages.selected : (row.analysis?.item.pages_kept || result.item.pages_kept || []),
   };
   const fields = emptyFields();
   const overridden: typeof SERVITUT_ANALYSIS_FIELDS[number][] = [];
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const previous = row.fields[field];
      fields[field] = {
         ...rebuildFieldSlot(
            previous,
            item,
            field,
            isFetched(row),
            Boolean(item.case_description?.trim()),
         ),
         aiStatus: result.fieldStatuses[field],
         aiText: result.fieldTexts[field],
         acceptedNoteId: previous.acceptedNoteId,
         acceptedText: previous.acceptedText,
      };
      const accepted = (previous.acceptedText || "").trim();
      const nextText = (result.fieldTexts[field] || "").trim();
      if (
         previous.acceptedNoteId
         && nextText !== accepted
         && (nextText || result.fieldStatuses[field] !== "not_run")
      ) {
         overridden.push(field);
      }
   });
   const next: ServitutRow = {
      ...row,
      job: "idle",
      review: row.review,
      analysis: { item, stale: false },
      fields,
   };
   return overridden.length ? markAnalysisFieldOverrides(next, overridden) : next;
}

function applyPaatale(row: ServitutRow, item: LLMAnalysisResultItem): ServitutRow {
   const merged: LLMAnalysisResultItem = {
      ...row.analysis?.item,
      ...item,
      id: item.id || row.id,
      pages_kept: row.pages.selected.length ? row.pages.selected : (row.analysis?.item.pages_kept || item.pages_kept || []),
      paataleberettiget: row.analysis?.item.paataleberettiget || item.paataleberettiget,
      paataleberettiget_resolution: item.paataleberettiget_resolution || row.analysis?.item.paataleberettiget_resolution,
   };
   const previous = row.fields.entitledParty;
   const nextText = getAiFieldText("entitledParty", merged);
   const next: ServitutRow = {
      ...row,
      analysis: { item: merged, stale: Boolean(row.analysis?.stale) },
      fields: {
         ...row.fields,
         entitledParty: {
            ...previous,
            aiStatus: statusFromConfidence(
               merged.paataleberettiget_confidence,
               false,
               Boolean(merged.paataleberettiget || merged.paataleberettiget_resolution),
               isFetched(row),
            ),
            aiText: nextText,
            acceptedNoteId: previous.acceptedNoteId,
            acceptedText: previous.acceptedText,
         },
      },
   };
   const accepted = (previous.acceptedText || "").trim();
   if (previous.acceptedNoteId && nextText.trim() !== accepted) {
      return markAnalysisFieldOverrides(next, ["entitledParty"]);
   }
   return next;
}

/** Saving is the in-flight ANN write. Its result must apply; only locate/analyze block it. */
function canApplyPersistedWrite(row: ServitutRow): boolean {
   return row.job === "idle" || row.job === "saving";
}

function resetDerived(row: ServitutRow): ServitutRow {
   return {
      ...row,
      analysis: undefined,
      fields: emptyFields(),
      review: { state: "unchecked" },
      pages: { ...row.pages, selected: row.pages.selected, extent: row.pages.selected.length ? "selection" : row.pages.extent },
   };
}

export function servitutReportReducer(
   state: ServitutReportState = initialServitutReportState(),
   action: ServitutReportAction,
): ServitutReportState {
   switch (action.type) {
      case ServitutReportActionType.LoadStarted:
         return {
            ...initialServitutReportState({ phase: state.phase, caseDescription: state.caseDescription }),
            status: "loading",
            loadProgress: action.payload || { stage: "registry", completed: 0, total: 1 },
         };
      case ServitutReportActionType.LoadProgress:
         return { ...state, loadProgress: action.payload };
      case ServitutReportActionType.LoadFailed:
         return { ...state, status: "error", loadError: action.payload, loadProgress: null };
      case ServitutReportActionType.ReportPrepared: {
         const rows: Record<string, ServitutRow> = {};
         action.payload.rows.forEach((row) => {
            rows[row.id] = withDeferredAktFetchJob(row);
         });
         return {
            ...state,
            status: "ready",
            loadProgress: null,
            loadError: undefined,
            rows,
            rowOrder: action.payload.rows.map((row) => row.id),
            columns: action.payload.columns,
            columnLabels: action.payload.columnLabels,
            columnDetails: action.payload.columnDetails,
            uniqueAktCount: action.payload.uniqueAktCount,
            metadataById: action.payload.metadataById,
            aktSearchByBfe: action.payload.aktSearchByBfe,
            caseDescription: action.payload.caseDescription ?? state.caseDescription,
            aktFetchIds: fetchingRowIds(rows),
         };
      }
      case ServitutReportActionType.AnnHydrated: {
         const rows = { ...state.rows };
         action.payload.rows.forEach((row) => {
            rows[row.id] = withDeferredAktFetchJob(row);
         });
         return settleAktFetchBatch(mergeAktFetchIds({ ...state, rows }, fetchingRowIds(rows)));
      }
      case ServitutReportActionType.AnnRecordsChanged: {
         const rows = { ...state.rows };
         state.rowOrder.forEach((id) => {
            const row = rows[id];
            if (row) {
               rows[id] = projectAnnOntoRow(row, action.payload.records, {
                  hasCaseDescription: Boolean(state.caseDescription.trim()),
                  protectUncommitted: true,
               });
            }
         });
         return { ...state, rows };
      }
      case ServitutReportActionType.BindLandRegistry: {
         const { rowId, document, pdfStatus, sourceMessage, metadata } = action.payload;
         let next = mapRow(state, rowId, (row) => {
            const imported = row.document.kind === "annotation" || row.document.kind === "upload";
            const job = row.job === "fetching" ? "idle" : row.job;
            if (imported) {
               return {
                  ...row,
                  registryBinding: registryBindingFor(document, row.registryBinding),
                  job,
               };
            }
            return {
               ...row,
               document,
               registryBinding: registryBindingFor(document, row.registryBinding),
               pdfStatus,
               sourceMessage,
               job,
            };
         });
         if (metadata) {
            next = { ...next, metadataById: { ...next.metadataById, [rowId]: metadata } };
         }
         return settleAktFetchBatch(next);
      }
      case ServitutReportActionType.FetchAktStarted:
         return setJobs(state, action.payload.rowIds, "fetching");
      case ServitutReportActionType.FetchAktFailed:
         return settleAktFetchBatch(setJobs(state, action.payload.rowIds, "idle"));
      case ServitutReportActionType.AktFetchBatchStarted:
         return mergeAktFetchIds(setJobs(state, action.payload.rowIds, "fetching"), action.payload.rowIds);
      case ServitutReportActionType.AktFetchBatchStopped: {
         const rowIds = action.payload?.rowIds?.length ? action.payload.rowIds : state.aktFetchIds;
         return {
            ...setJobs(state, rowIds.filter((id) => state.rows[id]?.job === "fetching"), "idle"),
            aktFetchIds: [],
         };
      }
      case ServitutReportActionType.SelectAkt: {
         const { rowId, document, pdfStatus, sourceMessage, metadata } = action.payload;
         let next = mapRow(state, rowId, (row) => {
            if (row.job !== "idle" && row.job !== "fetching") {
               return row;
            }
            return {
               ...row,
               document,
               registryBinding: registryBindingFor(document, row.registryBinding),
               pdfStatus,
               sourceMessage,
               analysis: undefined,
               fields: emptyFields(),
               review: { state: "unchecked" },
               pages: emptyPageModel(),
               job: "idle",
            };
         });
         if (metadata) {
            next = { ...next, metadataById: { ...next.metadataById, [rowId]: metadata } };
         }
         return settleAktFetchBatch(next);
      }
      case ServitutReportActionType.UploadPdf: {
         const next = mapRow(state, action.payload.rowId, (row) => {
            const document = { kind: "upload" as const, s3Key: action.payload.s3Key, filename: action.payload.filename };
            return {
               ...row,
               document,
               registryBinding: registryBindingFor(document, row.registryBinding),
               pdfStatus: "ready",
               analysis: undefined,
               fields: emptyFields(),
               review: { state: "unchecked" },
               pages: pageModelFromLists(action.payload.selectedPages, [], action.payload.selectedPages.length ? "selection" : "missing"),
               job: "idle",
               sourceMessage: "Using uploaded PDF",
            };
         });
         if (!action.payload.metadata) {
            return settleAktFetchBatch(next);
         }
         return settleAktFetchBatch({
            ...next,
            metadataById: { ...next.metadataById, [action.payload.rowId]: action.payload.metadata },
         });
      }
      case ServitutReportActionType.LocateStarted:
         return setJobs({
            ...state,
            unsavedIds: addIds(state.unsavedIds, action.payload.rowIds),
            justSavedIds: removeIds(state.justSavedIds, action.payload.rowIds),
         }, action.payload.rowIds, "locating");
      case ServitutReportActionType.LocateSucceeded: {
         let next = state;
         action.payload.results.forEach((result) => {
            next = mapRow(next, result.rowId, (row) => applyLocate(row, result.item));
         });
         return next;
      }
      case ServitutReportActionType.LocateFailed:
         return setJobs(state, action.payload.rowIds, "idle");
      case ServitutReportActionType.SelectPages:
         return mapRow(state, action.payload.rowId, (row) => {
            const aktPages = normalizeAktPages(action.payload.aktPages);
            if (aktPagesEqual(row.pages.selected, aktPages)) {
               return row;
            }
            const hadDerived = Boolean(row.analysis && !row.analysis.stale) || SERVITUT_ANALYSIS_FIELDS.some((field) => row.fields[field].acceptedNoteId);
            return {
               ...row,
               pages: { ...row.pages, selected: aktPages, extent: aktPages.length ? "selection" : "missing" },
               review: { state: "unchecked" },
               analysis: row.analysis ? { ...row.analysis, stale: hadDerived || row.analysis.stale } : undefined,
               fields: hadDerived ? emptyFields() : row.fields,
            };
         });
      case ServitutReportActionType.ApprovePages:
         return mapRow(state, action.payload.rowId, (row) => {
            if (row.job !== "idle" || !isFetched(row)) {
               return row;
            }
            const digital = isDigitalRow(row);
            if (!digital && !row.pages.selected.length) {
               return row;
            }
            const differs = !digital && !aktPagesEqual(row.pages.selected, row.pages.aiSuggested) && row.pages.aiSuggested.length > 0;
            const selected = row.pages.selected;
            return {
               ...row,
               pages: {
                  ...row.pages,
                  selected,
                  extent: digital && !selected.length ? "entire_akt" : (selected.length ? "selection" : row.pages.extent),
               },
               review: { state: "checked", origin: action.payload.origin },
               analysis: differs ? undefined : row.analysis,
               fields: differs ? emptyFields() : row.fields,
            };
         });
      case ServitutReportActionType.RejectPages:
         return mapRow(state, action.payload.rowId, (row) => {
            if (!canApplyPersistedWrite(row) || row.review.state === "rejected") {
               return row;
            }
            return { ...row, review: { state: "rejected", origin: action.payload.origin } };
         });
      case ServitutReportActionType.RevertPages:
         return mapRow(state, action.payload.rowId, (row) => {
            if (!canApplyPersistedWrite(row)) {
               return row;
            }
            if (row.review.state === "rejected") {
               return { ...row, review: { state: "unchecked" } };
            }
            if (row.review.state !== "checked") {
               return row;
            }
            const restored = row.registryBinding;
            return {
               ...resetDerived(row),
               document: restored || (row.document.kind === "rigsarkivet" ? row.document : { kind: "none" }),
               pdfStatus: restored ? "ready" : (needsLandRegistryRebind({ ...row, document: { kind: "none" }, registryBinding: undefined }) ? "pending" : row.pdfStatus),
               pages: emptyPageModel(),
               stedfaestelseId: row.stedfaestelseId,
               sourceMessage: undefined,
            };
         });
      case ServitutReportActionType.ContentStarted:
         return setJobs({
            ...state,
            unsavedIds: addIds(state.unsavedIds, action.payload.rowIds),
            justSavedIds: removeIds(state.justSavedIds, action.payload.rowIds),
         }, action.payload.rowIds, "analyzing");
      case ServitutReportActionType.ContentSucceeded: {
         let next = state;
         action.payload.results.forEach((result) => {
            next = mapRow(next, result.rowId, (row) => applyContent(row, result));
         });
         return next;
      }
      case ServitutReportActionType.ContentFailed:
         return setJobs(state, action.payload.rowIds, "idle");
      case ServitutReportActionType.PaataleResolved: {
         const next = mapRow(state, action.payload.rowId, (row) => applyPaatale(row, action.payload.item));
         return {
            ...next,
            unsavedIds: addIds(next.unsavedIds, [action.payload.rowId]),
            justSavedIds: removeIds(next.justSavedIds, [action.payload.rowId]),
         };
      }
      case ServitutReportActionType.AcceptField:
         return mapRow(state, action.payload.rowId, (row) => clearAnalysisFieldOverride({
            ...row,
            fields: {
               ...row.fields,
               [action.payload.field]: {
                  ...row.fields[action.payload.field],
                  aiStatus: "person_approved",
                  acceptedNoteId: action.payload.noteId,
                  acceptedText: action.payload.text,
               },
            },
         }, action.payload.field));
      case ServitutReportActionType.RevertField:
         return mapRow(state, action.payload.rowId, (row) => clearAnalysisFieldOverride({
            ...row,
            fields: {
               ...row.fields,
               [action.payload.field]: {
                  ...row.fields[action.payload.field],
                  acceptedNoteId: undefined,
                  acceptedText: undefined,
                  aiStatus: action.payload.restoredStatus
                     || rebuildFieldSlot(emptyFieldSlot(), row.analysis?.item, action.payload.field, isFetched(row), Boolean(state.caseDescription.trim())).aiStatus,
               },
            },
         }, action.payload.field));
      case ServitutReportActionType.ResetField:
         return mapRow(state, action.payload.rowId, (row) => clearAnalysisFieldOverride(row, action.payload.field));
      case ServitutReportActionType.SetPhase:
         return { ...state, phase: action.payload };
      case ServitutReportActionType.SetCaseDescription:
         return { ...state, caseDescription: action.payload };
      case ServitutReportActionType.SetAnalysisField:
         return { ...state, analysisField: action.payload };
      case ServitutReportActionType.CommitStarted:
         return setJobs(state, action.payload.rowIds, "saving");
      case ServitutReportActionType.CommitSucceeded: {
         const idleIds = action.payload.rowIds
            ?? state.rowOrder.filter((id) => state.rows[id]?.job === "saving");
         let next = setJobs(state, idleIds, "idle");
         const records = action.payload.records;
         if (records.length) {
            const rows = { ...next.rows };
            const projectIds = action.payload.rowIds ?? next.rowOrder;
            projectIds.forEach((id) => {
               if (rows[id]) {
                  rows[id] = projectAnnOntoRow(rows[id], records, {
                     hasCaseDescription: Boolean(next.caseDescription.trim()),
                  });
               }
            });
            next = { ...next, rows };
         }
         return next;
      }
      case ServitutReportActionType.CommitFailed:
         return setJobs(state, action.payload.rowIds, "idle");
      case ServitutReportActionType.SetOpeningRow:
         return { ...state, openingRowId: action.payload };
      case ServitutReportActionType.SetExporting:
         return { ...state, exporting: action.payload.exporting, exportProgress: action.payload.progress };
      case ServitutReportActionType.SetResolvingPaatale: {
         const resolvingPaataleIds = action.payload.resolving
            ? addIds(state.resolvingPaataleIds, [action.payload.rowId])
            : removeIds(state.resolvingPaataleIds, [action.payload.rowId]);
         return { ...state, resolvingPaataleIds };
      }
      case ServitutReportActionType.AttachStedfaestelse:
         return mapRow(state, action.payload.rowId, (row) => ({
            ...row,
            stedfaestelseId: action.payload.stedfaestelseId,
         }));
      case ServitutReportActionType.MarkUnsaved:
         return {
            ...state,
            unsavedIds: addIds(state.unsavedIds, action.payload.rowIds),
            justSavedIds: removeIds(state.justSavedIds, action.payload.rowIds),
         };
      case ServitutReportActionType.MarkSaved:
         return {
            ...state,
            justSavedIds: addIds(state.justSavedIds, action.payload.rowIds),
            unsavedIds: removeIds(state.unsavedIds, action.payload.rowIds),
         };
      default:
         return state;
   }
}
